/**
 * Load one read-only view's rows, with the four states every screen owes.
 *
 * Both hierarchies fetch a flat list and re-fetch it after a write, and the
 * difference between them is one function reference. Written once so the two
 * cannot drift into disagreeing about what "still loading" looks like.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../../api'

export interface DiagramRows<T> {
  /** Null until the first answer arrives — that is the loading state. */
  rows: T[] | null
  error: ApiError | null
  /** Re-read after a write. Keeps the current rows on screen while it runs. */
  reload: () => void
}

/**
 * @param[in] fetcher - () => Promise : the api.ts method to call
 * @return DiagramRows
 */
export function useDiagramRows<T>(fetcher: () => Promise<T[]>): DiagramRows<T> {
  const [rows, setRows] = useState<T[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  // Held in a ref rather than depended on. Every caller passes an api.ts method,
  // which is a stable reference on a module-level object - but the first caller
  // to pass an inline arrow would otherwise get a new `load` every render and
  // fetch in a loop, and the fix for that must not be an ignored lint rule.
  const fetch = useRef(fetcher)
  fetch.current = fetcher

  const load = useCallback(() => {
    fetch
      .current()
      .then((answer) => {
        setRows(answer)
        setError(null)
      })
      .catch((failure: ApiError) => setError(failure))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return { rows, error, reload: load }
}
